import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getSubsectionHeadingSizeClass, getSmallTextSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';

const PrivacyPolicy: React.FC = () => {
  const { fontSize } = useFontSize();
  
  useEffect(() => {
    document.title = 'Stock Table - Privacy Policy';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />
      
      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8 md:p-10">
          <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary mb-2`}>
            Privacy Policy
          </h1>
          <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary mb-8`}>
            Last Updated: January 21, 2026
          </p>

          <div className="prose prose-sm sm:prose-base max-w-none text-text-light-primary dark:text-text-primary">
            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                1. Introduction
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Stock Table ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we 
                collect, use, disclose, and safeguard your information when you use our Service. Please read this Privacy Policy 
                carefully. By using Stock Table, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                2. Information We Collect
              </h2>
              
              <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3 mt-6`}>
                2.1. Information You Provide
              </h3>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We collect information that you provide directly to us, including:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li><strong>Account Information:</strong> Email address, password (hashed), and authentication credentials when you create an account</li>
                <li><strong>Profile Information:</strong> Information you choose to provide in your account profile</li>
                <li><strong>User Content:</strong> Ticker lists, watchlists, preferences, and any other content you create or save</li>
                <li><strong>Communication Data:</strong> Information you provide when contacting us for support or feedback</li>
              </ul>

              <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3 mt-6`}>
                2.2. Information Collected Automatically
              </h3>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                When you use our Service, we automatically collect certain information, including:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li><strong>Usage Data:</strong> Information about how you interact with the Service, including pages visited, features used, and time spent</li>
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers, and IP address</li>
                <li><strong>Log Data:</strong> Server logs, error reports, and performance data</li>
                <li><strong>Location Data:</strong> General location information derived from your IP address (not precise location)</li>
              </ul>

              <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3 mt-6`}>
                2.3. Information from Third-Party Services
              </h3>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you sign in using Google Sign-In, we receive information from Google, including:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Email address</li>
                <li>Google account ID</li>
                <li>Email verification status</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                3. How We Use Your Information
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We use the information we collect to:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Provide, maintain, and improve our Service</li>
                <li>Authenticate your identity and manage your account</li>
                <li>Save and retrieve your ticker lists, preferences, and settings</li>
                <li>Enable public list sharing functionality when you choose to share lists</li>
                <li>Send you service-related communications, including account verification emails</li>
                <li>Monitor and analyze usage patterns to improve user experience</li>
                <li>Detect, prevent, and address technical issues, security threats, or fraudulent activity</li>
                <li>Comply with legal obligations and enforce our Terms of Service</li>
                <li>Respond to your inquiries, comments, or feedback</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                4. Cookies and Tracking Technologies
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We use cookies and similar tracking technologies to track activity on our Service and store certain information. 
                Cookies are files with a small amount of data that may include an anonymous unique identifier.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Types of cookies we use:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li><strong>Essential Cookies:</strong> Required for the Service to function properly, including authentication and security</li>
                <li><strong>Preference Cookies:</strong> Remember your settings and preferences, such as theme selection</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our Service</li>
              </ul>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you 
                do not accept cookies, you may not be able to use some portions of our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                5. Data Sharing and Disclosure
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              
              <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3 mt-6`}>
                5.1. Public List Sharing
              </h3>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you choose to make a ticker list public, the list name, ticker symbols, and any public list name you provide 
                will be accessible to anyone with the public link. Your email address and other personal information are not 
                shared through public lists.
              </p>

              <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3 mt-6`}>
                5.2. Service Providers
              </h3>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We may share information with third-party service providers who perform services on our behalf, such as:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Hosting and cloud storage providers</li>
                <li>Email service providers for account verification and communications</li>
                <li>Billing and payment handling</li>
              </ul>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                These service providers are contractually obligated to protect your information and use it only for the purposes 
                we specify.
              </p>

              <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3 mt-6`}>
                5.3. Legal Requirements
              </h3>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We may disclose your information if required to do so by law or in response to valid requests by public 
                authorities (e.g., a court or government agency).
              </p>

              <h3 className={`${getSubsectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-3 mt-6`}>
                5.4. Business Transfers
              </h3>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that 
                transaction. We will notify you of any such change in ownership or control of your personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                6. Data Security
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We implement appropriate technical and organizational security measures to protect your personal information against 
                unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Encryption of passwords using industry-standard hashing algorithms</li>
                <li>Secure transmission of data using HTTPS/TLS encryption</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Secure storage of data in protected databases</li>
              </ul>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use 
                commercially acceptable means to protect your personal information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                7. Data Retention
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We retain your personal information for as long as necessary to provide you with our Service and fulfill the 
                purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                When you delete your account, we will delete or anonymize your personal information, except where we are required 
                to retain it for legal, regulatory, or legitimate business purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                8. Your Rights and Choices
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information and account</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Objection:</strong> Object to certain processing of your personal information</li>
                <li><strong>Withdrawal of Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                To exercise these rights, please contact us through the contact form provided in the Service or by emailing us at{' '}
                <a 
                  href="mailto:service@stock-table.com"
                  className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
                >
                  service@stock-table.com
                </a>
                . We will respond to your request within a reasonable timeframe.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                9. Children's Privacy
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Our Service is not intended for children under the age of 13 (or the applicable age of majority in your jurisdiction). 
                We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe 
                that your child has provided us with personal information, please contact us, and we will delete such information 
                from our systems.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                10. International Data Transfers
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Your information may be transferred to and processed in countries other than your country of residence. These countries 
                may have data protection laws that differ from those in your country. By using our Service, you consent to the 
                transfer of your information to these countries.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                11. Third-Party Links and Services
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Our Service may contain links to third-party websites or services that are not owned or controlled by Stock Table. 
                We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy 
                policies of any third-party services you access.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                12. California Privacy Rights
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you are a California resident, you may have additional rights under the California Consumer Privacy Act (CCPA), 
                including:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>The right to know what personal information we collect, use, and disclose</li>
                <li>The right to delete personal information we have collected</li>
                <li>The right to opt-out of the sale of personal information (we do not sell personal information)</li>
                <li>The right to non-discrimination for exercising your privacy rights</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                13. European Privacy Rights (GDPR)
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you are located in the European Economic Area (EEA), you have certain rights under the General Data Protection 
                Regulation (GDPR), including the rights listed in Section 8 above. Our legal basis for processing your personal 
                information includes:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Performance of a contract (providing the Service to you)</li>
                <li>Legitimate interests (improving our Service, security, fraud prevention)</li>
                <li>Consent (where you have provided it)</li>
                <li>Legal obligations (complying with applicable laws)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                14. Changes to This Privacy Policy
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy 
                Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically 
                for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                15. Contact Us
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you have any questions about this Privacy Policy or our privacy practices, please contact us through the contact form 
                provided in the Service or by emailing us at{' '}
                <a 
                  href="mailto:service@stock-table.com"
                  className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
                >
                  service@stock-table.com
                </a>
                .
              </p>
            </section>

            <div className="mt-10 pt-6 border-t border-border-light dark:border-border-color">
              <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary text-center`}>
                By using Stock Table, you acknowledge that you have read and understood this Privacy Policy.
              </p>
              <div className="mt-6 text-center">
                <Link
                  to="/"
                  className={`inline-block px-6 py-2 bg-primary dark:bg-highlight-gold text-gray-900 dark:text-gray-900 rounded-lg font-medium hover:opacity-90 transition-opacity ${getTextSizeClass(fontSize)}`}
                >
                  Return to Stock Table
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

